import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import { selectAccessToken } from 'services/session/sessionSelectors';
import { clearSession, refreshApToken } from 'services/session/sessionAsync';
import { selectBrandCode } from 'services/brand/brandSelectors';
import {
  API_ENDPOINTS,
  API_REQUEST_METHODS,
  GENERAL,
  WITH_BEARER_TOKEN,
  WITH_OUT_BEARER_TOKEN,
} from 'utils/constants';
import { RootState, store } from 'app/store';
import api from './apiInstance';

const TRY_AGAIN_ERR_MSG = 'Something went wrong, please try again later.';

/**
 * Matches only the five-step enrollment calls: the bare collection URL (account creation) and
 * its `?enrollmentStep=` PATCHes.
 *
 * `API_ENDPOINTS.fiveStepEnrollment` is `api/client/enrollments`, a *prefix* of the thin, flex,
 * `/info` and `/validation` endpoints — so a substring test would silently change the error
 * shape every other enrollment flow receives. Anchoring on `?` or end-of-string keeps it to
 * the five-step calls alone.
 */
const FIVE_STEP_ENROLLMENT_URL_PATTERN = new RegExp(
  `${API_ENDPOINTS.fiveStepEnrollment}(\\?|$)`,
);

/**
 * The family-plan invitation lookup, which routes on the response *status* (400/406/410/416/500
 * each mean a different thing to the member) and so cannot lose it to the flattening below.
 *
 * Matched on `?token=` because the same `/enrollments/info` path also serves the offer lookup
 * used by thin, flex and five-step, which must keep receiving a plain message.
 */
const PARTICIPANT_TOKEN_URL_PATTERN = /enrollments\/info\?token=/;

/**
 * The AOL / partner pre-enroll token check, which reports an unusable token as a 400 carrying
 * `tokenStatus` — the field that decides whether the member sees "already active" or the generic
 * failure panel, and which the flattening below would discard.
 *
 * Anchored on end-of-path so it stays this one endpoint.
 */
const YAHOO_TOKEN_URL_PATTERN = new RegExp(
  `${API_ENDPOINTS.yahooTokenValidation}$`,
);

/**
 * Thin enrollment's account call, which branches on the same fields as every other enrollment
 * flow — a duplicate email (`property`), a brand mismatch, and the PID failures.
 *
 * Anchored on end-of-path. It sits *under* `api/client/enrollments`, so the five-step pattern
 * above deliberately does not reach it, and this must not reach anything below it either.
 */
const THIN_ENROLLMENT_URL_PATTERN = new RegExp(
  `${API_ENDPOINTS.thinEnrollment}$`,
);

function includeBrandCode(
  headers: AxiosRequestConfig['headers'],
  brandCode: string,
) {
  if (headers) {
    headers['X-Idg-Brand'] = brandCode;
  }
}

const includeAuthorizationHeader = (
  configURL: string | undefined,
  headers: AxiosRequestConfig['headers'],
  accessToken: string | null,
) => {
  if (!configURL) return;
  /* A caller that set its own Authorization means it, and it is never the
     session token: `api/client/sessions/redeem` carries the fixed Pango redeem
     credential and runs while the user is still signed out. It used to be safe
     by omission — `auth/session/redeem` matched nothing in either list — but
     its replacement contains `api/client/sessions` (WITH_BEARER_TOKEN) and so
     matches by substring, which would replace the credential with `Bearer ` +
     an empty access token. */
  if (headers?.Authorization) return;
  WITH_BEARER_TOKEN.forEach((url) => {
    if (headers && configURL.includes(url)) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  });
  WITH_OUT_BEARER_TOKEN.forEach((url) => {
    if (headers && configURL.includes(url)) {
      headers.Authorization = accessToken;
    }
  });
};

// Intercept all responses
let isRefreshing = false;
let failedQueue: any[] = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;

// Function to process the queue
const processQueue = (error: unknown, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      error?.response?.status === 401 &&
      !originalRequest.retryAttempt &&
      !originalRequest.url.includes('/2fa') &&
      !originalRequest.url.endsWith('/refresh') &&
      !originalRequest.url.endsWith('/sessions') &&
      /* Unauthenticated reset-password endpoints: 401 here is a
       * business signal ("wrong identity"), not a session-expired
       * signal. Auto-refreshing would silently re-fire the request up
       * to MAX_REFRESH_ATTEMPTS + 1 times, incrementing the BE's
       * 5-attempt lockout counter on every user click and causing the
       * account-locked bounce to trip on click 2 or 3 instead of 5. */
      !originalRequest.url.includes('unverified_users/verify') &&
      !originalRequest.url.includes('unverified_users/validate')
    ) {
      originalRequest.retryAttempt = true;
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await store.dispatch(refreshApToken());
          const appStore = store.getState() as RootState;
          const accessToken = selectAccessToken(appStore);
          const brandCode = selectBrandCode(appStore);
          includeBrandCode(originalRequest.headers, brandCode);
          includeAuthorizationHeader(
            originalRequest.url,
            originalRequest.headers,
            accessToken,
          );
          return api(originalRequest);
        } catch (refreshError) {
          refreshAttempts += 1;
          if (refreshAttempts <= MAX_REFRESH_ATTEMPTS) {
            processQueue(refreshError);
            return api(originalRequest);
          }
          store.dispatch(clearSession());
          throw refreshError;
          // Handle token refresh failure
          // Redirect to login page or handle error
        } finally {
          isRefreshing = false;
          refreshAttempts = 0;
        }
      } else {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Retry the original request
            return api(originalRequest);
          })
          .catch((err) => {
            throw err;
          });
      }
    }
    return Promise.reject(error);
  },
);

// End Intercept all responses

// Start - Interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error.message);
    }

    const axiosErr = error as AxiosError;

    // let errMsg = axiosErr?.message || GENERAL.defaultErrorMsg;
    let errMsg = GENERAL.defaultErrorMsg;

    if (axiosErr.response) {
      const { data } = axiosErr.response;
      const responseData = Array.isArray(data) ? data[0] : data;
      const resData = responseData as {
        errorMessage?: string;
        message?: string;
        code?: string;
        errorCode?: number | string;
        failureCode?: string;
        daysUntilRetry?: string | number;
      };

      if (axiosErr.config?.url?.includes('id_verification/tuid')) {
        const tuidFailure = new Error(
          resData?.message ?? 'TUID verification request failed',
        ) as Error & {
          status: number;
          errorCode?: string;
          failureCode?: string;
          daysUntilRetry?: string | number;
        };
        tuidFailure.status = axiosErr.response.status;
        if (typeof resData?.errorCode === 'string') {
          tuidFailure.errorCode = resData.errorCode;
        }
        tuidFailure.failureCode = resData?.failureCode;
        tuidFailure.daysUntilRetry = resData?.daysUntilRetry;
        return Promise.reject(tuidFailure);
      }

      if (
        axiosErr.config?.url?.includes('id_verification') ||
        axiosErr.config?.url?.includes('credit_reports') ||
        axiosErr.config?.url?.includes('credit-reports') ||
        axiosErr.config?.url?.includes('security-freeze') ||
        axiosErr.config?.url?.includes('healthlock') ||
        // SSNT needs the raw status: 404 means "no records found for this child"
        // (a normal state with its own screen), not a failure. Matched on the full
        // path — a bare `public-records` would also capture `public-records/DEED`,
        // which expects the display-copy string built below.
        axiosErr.config?.url?.includes('public-records/SSNT') ||
        /* Flex enrollment routes on `property` and `errorCode`, which the string
         * flattening below discards. Scoped to `enrollments/flex` so the thin
         * and light flows keep receiving a plain display message. */
        axiosErr.config?.url?.includes(
          API_ENDPOINTS.flexComplimentaryEnrollment,
        ) ||
        /* Five-step enrollment routes on the same two fields: a duplicate email
         * (`property`), a brand mismatch, a DIT identity denial and the PID
         * verificationFailed / securityFreeze failures all branch on them. */
        FIVE_STEP_ENROLLMENT_URL_PATTERN.test(axiosErr.config?.url ?? '') ||
        PARTICIPANT_TOKEN_URL_PATTERN.test(axiosErr.config?.url ?? '') ||
        /* The AOL token check branches on `tokenStatus` in a 400 body. */
        YAHOO_TOKEN_URL_PATTERN.test(axiosErr.config?.url ?? '') ||
        THIN_ENROLLMENT_URL_PATTERN.test(axiosErr.config?.url ?? '') ||
        /* Notification settings routes on the failure body: the recipients family reports the
           reason in `message`, the voice family in `code`. Flattening either to one sentence
           loses the distinction the code-entry steps must show, so both take the raw envelope
           and map it in `services/notification-settings/utils.ts` — as identityWeb does. */
        axiosErr.config?.url?.includes('api/client/recipients/') ||
        axiosErr.config?.url?.includes(API_ENDPOINTS.notificationSettings) ||
        /* Reset-password flow branches on raw HTTP status:
         *   • `/unverified_users/validate` — 400/418/other route to
         *     different `warning-message=…` variants on /sign-in.
         *   • `/unverified_users/verify`  — 401/403 + response body
         *     `message === 'Account temporarily locked'` decides
         *     retry vs. Angular's 5-attempt `account-locked` bounce. */
        axiosErr.config?.url?.includes('unverified_users/validate') ||
        axiosErr.config?.url?.includes('unverified_users/verify') ||
        /* Billing account lookup: 404 is a real signal ("no Zuora
         * account exists yet" — the Discover-employee proprietary-
         * card program). Caller derives `accountFound: false` from
         * the status, so the interceptor must preserve it. */
        axiosErr.config?.url?.includes('pango-billing/client/account/info') ||
        /* DBOO user fetch: 404 means the member hasn't enrolled in
         * data-broker opt-out yet. The page's caller uses the status
         * to suppress the "DBOO user not found" toast for first-time
         * visitors (the UI falls back to NoActiveOptOuts). */
        axiosErr.config?.url?.includes('dboo/v2/user')
      ) {
        return Promise.reject(
          JSON.stringify({
            status: axiosErr.response.status,
            data: responseData,
          }),
        );
      }

      const defaultErrMsg =
        resData?.errorMessage ||
        resData?.message ||
        resData?.code ||
        GENERAL.defaultErrorMsg;

      if (
        axiosErr.config?.url &&
        (axiosErr.config.url.includes('/2fa') ||
          axiosErr.config.url.endsWith('/sessions') ||
          axiosErr.config.url.endsWith('/refresh'))
      ) {
        return Promise.reject(axiosErr.response?.status);
      }

      switch (axiosErr.response?.status) {
        case 400:
          if (resData?.errorCode === 418) {
            errMsg = resData.errorCode.toString();
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        case 404:
          if (resData?.message === 'HTTP 404 Not Found') {
            errMsg = TRY_AGAIN_ERR_MSG;
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        case 500:
          if (resData?.message === 'Internal Server Error') {
            errMsg = TRY_AGAIN_ERR_MSG;
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        case 504:
          if (resData?.message === 'Endpoint request timed out') {
            errMsg = TRY_AGAIN_ERR_MSG;
          } else {
            errMsg = defaultErrMsg;
          }
          break;
        default:
          errMsg = defaultErrMsg;
          break;
      }
    }
    // Handle response errors
    return Promise.reject(errMsg);
  },
);
// End - Interceptor to handle errors

/**
 * Common function to make API request
 */

async function makeAPIRequest(
  config: AxiosRequestConfig,
  method: string,
  accessToken: string | null,
  brandCode: string,
) {
  const headers = config?.headers ? { ...config?.headers } : {};
  includeBrandCode(headers, brandCode);
  includeAuthorizationHeader(config.url, headers, accessToken);

  try {
    const response = await api({ ...config, method, headers });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
}

const getAPI = createAsyncThunk(
  API_REQUEST_METHODS.get.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.get.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const postAPI = createAsyncThunk(
  API_REQUEST_METHODS.post.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.post.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const putAPI = createAsyncThunk(
  API_REQUEST_METHODS.put.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.put.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const patchAPI = createAsyncThunk(
  API_REQUEST_METHODS.patch.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.patch.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const deleteAPI = createAsyncThunk(
  API_REQUEST_METHODS.delete.name,
  async (config: AxiosRequestConfig, thunkApi) => {
    const appStore = thunkApi.getState() as RootState;

    try {
      const accessToken = selectAccessToken(appStore);
      const brandCode = selectBrandCode(appStore);
      const response = await makeAPIRequest(
        config,
        API_REQUEST_METHODS.delete.method,
        accessToken,
        brandCode,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export { getAPI, postAPI, putAPI, patchAPI, deleteAPI };
